/**
 * Vector3ControlUI - 三维向量控制组件
 * 包含三个并排的数值输入框，用于控制 X, Y, Z 坐标
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { NumericInputUI, NumericInputOptions } from '../ui/numericInputUI';

export interface Vector3ValueOptions {
  label?: string;
  /** 初始值 */
  value?: number;
  /** 最小值 */
  min?: number;
  /** 最大值 */
  max?: number;
  /** 步长 */
  step?: number;
  /** 精度（小数位数） */
  precision?: number;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否只读 */
  readonly?: boolean;
  /** 是否显示当前值 */
  showValue?: boolean;
  /** 值显示格式 */
  valueFormat?: (value: number) => string;
}

export interface Vector3ControlOptions extends ElementOptions {
  /** 标签文本 */
  label?: string;
  valueOptions?: Map<string, Vector3ValueOptions>;
  /** 容器样式类 */
  containerClass?: string;
  /** 标签样式类 */
  labelClass?: string;
  /** 输入框容器样式类 */
  inputsContainerClass?: string;
  /** 单个输入框样式类 */
  inputClass?: string;
  /** 标签是否与输入框同行显示 */
  isLabelInline?: boolean;
}

export class Vector3ControlUI {
  private ui: BootstrapUI;
  private container: HTMLElement;
  private options: Vector3ControlOptions;
  private numericInputs: { x: NumericInputUI; y: NumericInputUI; z: NumericInputUI };
  private onChangeCallbacks: ((value: { x: number; y: number; z: number }) => void)[] = [];
  private inputsContainerElement: HTMLElement;

  constructor(options: Vector3ControlOptions = {}) {
    this.ui = bootstrapUI;
    this.options = {
      label: 'Vector3',
      valueOptions: new Map(),
      containerClass: 'panel-setting-item',
      labelClass: 'panel-setting-item-label',
      inputsContainerClass: 'panel-inputs-container',
      inputClass: 'vector3-input',
      isLabelInline: true,
      ...options
    };

    this.container = this.createElement();
    this.numericInputs = this.createNumericInputs();
    this.setupEventHandlers();
  }

  /**
   * 创建向量控制组件元素
   * @returns 包含三个输入框的容器元素
   */
  private createElement(): HTMLElement {
    const container = this.ui.createElement({
      tag: 'div',
      className: 'panel-setting-flex-item'
    });
    const labelElement = this.ui.createElement({
      tag: 'div',
      textContent: this.options.label || '',
      className: 'panel-setting-sub-title',
      i18nKey: this.options.i18nKey||'',
    });
    container.appendChild(labelElement);

    const inputsContainer = this.ui.createElement({
      tag: 'div',
      className: ['w100','d-flex','flex-gap-75'],
    });
    this.inputsContainerElement = inputsContainer;
    container.appendChild(inputsContainer);

    return container;
  }

  /**
   * 创建三个数值输入组件
   */
  private createNumericInputs(): { x: NumericInputUI; y: NumericInputUI; z: NumericInputUI } {
    const numericInputs = {
      x: null,
      y: null,
      z: null,
    };
    if (this.options.valueOptions) {
      this.options.valueOptions.forEach((valueOptions, key) => {
        const input = new NumericInputUI({
          label: '',
          value: 0,
          showValue: false,
          containerClass: ['panel-setting-item','numeric-input-flex-container'],
          labelClass: 'panel-setting-item-label',
          isLabelInline:this.options.isLabelInline,
          ...valueOptions,
        });
        this.inputsContainerElement.appendChild(input.getContainer());
        numericInputs[key as keyof typeof numericInputs] = input;
      });
    }

    return numericInputs;
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    // 为每个输入框添加变化监听
    this.numericInputs.x.onChange((value) => {
      this.notifyChange();
    });

    this.numericInputs.y.onChange((value) => {
      this.notifyChange();
    });

    this.numericInputs.z.onChange((value) => {
      this.notifyChange();
    });
  }

  /**
   * 通知值变化
   */
  private notifyChange(): void {
    const value = this.getValue();
    this.onChangeCallbacks.forEach(callback => {
      try {
        callback(value);
      } catch (error) {
        console.error('Vector3控制变化回调执行错误:', error);
      }
    });
  }

  /**
   * 获取当前值
   */
  getValue(): { x: number; y: number; z: number } {
    return {
      x: this.numericInputs.x.getValue() || 0,
      y: this.numericInputs.y.getValue() || 0,
      z: this.numericInputs.z.getValue() || 0
    };
  }

  /**
   * 设置当前值
   */
  setValue(value: { x: number; y: number; z: number }): void {
    this.numericInputs.x.setValue(value.x);
    this.numericInputs.y.setValue(value.y);
    this.numericInputs.z.setValue(value.z);
  }
  syncFromInput(value: { x: number; y: number; z: number }): void {
    this.numericInputs.x.syncFromInput(value.x);
    this.numericInputs.y.syncFromInput(value.y);
    this.numericInputs.z.syncFromInput(value.z);
  }
  /**
   * 设置 X 值
   */
  setX(value: number): void {
    this.numericInputs.x.setValue(value);
  }

  /**
   * 设置 Y 值
   */
  setY(value: number): void {
    this.numericInputs.y.setValue(value);
  }

  /**
   * 设置 Z 值
   */
  setZ(value: number): void {
    this.numericInputs.z.setValue(value);
  }

  /**
   * 获取 X 值
   */
  getX(): number {
    return this.numericInputs.x.getValue() || 0;
  }

  /**
   * 获取 Y 值
   */
  getY(): number {
    return this.numericInputs.y.getValue() || 0;
  }

  /**
   * 获取 Z 值
   */
  getZ(): number {
    return this.numericInputs.z.getValue() || 0;
  }

  /**
   * 设置最小值
   */
  setMin(min: number): void {
    this.numericInputs.x.setMin(min);
    this.numericInputs.y.setMin(min);
    this.numericInputs.z.setMin(min);
  }

  /**
   * 设置最大值
   */
  setMax(max: number): void {
    this.numericInputs.x.setMax(max);
    this.numericInputs.y.setMax(max);
    this.numericInputs.z.setMax(max);
  }

  /**
   * 设置步长
   */
  setStep(step: number): void {
    this.numericInputs.x.setStep(step);
    this.numericInputs.y.setStep(step);
    this.numericInputs.z.setStep(step);
  }

  /**
   * 设置精度
   */
  setPrecision(precision: number): void {
    this.numericInputs.x.setPrecision(precision);
    this.numericInputs.y.setPrecision(precision);
    this.numericInputs.z.setPrecision(precision);
  }

  /**
   * 是否禁用
   */
  isDisabled(): boolean {
    return this.numericInputs.x.isDisabled();
  }

  /**
   * 设置禁用状态
   */
  setDisabled(disabled: boolean): void {
    this.numericInputs.x.setDisabled(disabled);
    this.numericInputs.y.setDisabled(disabled);
    this.numericInputs.z.setDisabled(disabled);
  }

  /**
   * 设置组件激活状态（启用/禁用整个组件）
   */
  setActive(status: boolean): void {
    this.numericInputs.x.setActive(status);
    this.numericInputs.y.setActive(status);
    this.numericInputs.z.setActive(status);
  }

  /**
   * 是否只读
   */
  isReadOnly(): boolean {
    return this.numericInputs.x.isReadOnly();
  }

  /**
   * 设置只读状态
   */
  setReadOnly(readonly: boolean): void {
    this.numericInputs.x.setReadOnly(readonly);
    this.numericInputs.y.setReadOnly(readonly);
    this.numericInputs.z.setReadOnly(readonly);
  }

  /**
   * 添加值变化监听器
   */
  onChange(callback: (value: { x: number; y: number; z: number }) => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * 移除值变化监听器
   */
  offChange(callback: (value: { x: number; y: number; z: number }) => void): void {
    const index = this.onChangeCallbacks.indexOf(callback);
    if (index > -1) {
      this.onChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * 获取容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 获取数值输入组件
   */
  getNumericInputs(): { x: NumericInputUI; y: NumericInputUI; z: NumericInputUI } {
    return this.numericInputs;
  }

  /**
   * 获取标签文本
   */
  getLabel(): string {
    return this.options.label || '';
  }

  /**
   * 设置标签文本
   */
  setLabel(label: string): void {
    this.options.label = label;
    const labelElement = this.container.querySelector('label');
    if (labelElement) {
      labelElement.textContent = label;
    }
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.onChangeCallbacks = [];

    // 销毁数值输入组件
    this.numericInputs.x.destroy();
    this.numericInputs.y.destroy();
    this.numericInputs.z.destroy();

    // 移除DOM元素
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// 导出默认实例创建函数
export function createVector3ControlUI(options: Vector3ControlOptions = {}): Vector3ControlUI {
  return new Vector3ControlUI(options);
}
